import React, { useState } from "react";
import { msalInstance } from "../../../src/msalConfig";
import ICAL from "ical.js"; // Import the ical.js library

// Define the type for the event object
interface EventDetails {
  subject: string;
  start: {
    dateTime: string; // Use full date-time format (YYYY-MM-DDTHH:MM:SS)
    timeZone: string;
  };
  end: {
    dateTime: string; // Use full date-time format (YYYY-MM-DDTHH:MM:SS)
    timeZone: string;
  };
  body: {
    content: string;
    contentType: string;
  };
}

const OutlookEvent = () => {
  const [eventDetails, setEventDetails] = useState<EventDetails[]>([]); // State for storing parsed events
  const loginRequest = { scopes: ["Calendars.ReadWrite", "Mail.Send", "User.Read"] };

  // Utility to get the local time zone
  const getLocalTimeZone = () => {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  };

  // Function to handle both parsing the ICS file and adding events to the calendar
  const handleAddEvents = async () => {
    try {
      // Step 1: Parse the ICS file content directly (embedded as a string)
      const icsText = `
BEGIN:VCALENDAR
VERSION:2.0
// BEGIN:VEVENT
// SUMMARY:Test Day
// DTSTART;VALUE=DATE:20241226T16:20:00
// DTEND;VALUE=DATE:20241226T17:00:00
// DESCRIPTION:For Test
// END:VEVENT
// BEGIN:VEVENT
// SUMMARY:Test Day
// DTSTART;VALUE=DATE:20241227T16:20:00
// DTEND;VALUE=DATE:20241227T17:00:00
// DESCRIPTION:For Test
// END:VEVENT
BEGIN:VEVENT
SUMMARY:Holiday-New Year's Dayy
DTSTART;VALUE=DATE:20250101
DTEND;VALUE=DATE:20250102
DESCRIPTION:New Year's Day holiday
END:VEVENT
BEGIN:VEVENT
SUMMARY:Pongal Holiday
DTSTART;VALUE=DATE:20250114
DTEND;VALUE=DATE:20250115
DESCRIPTION:Holiday-Pongal 
END:VEVENT
BEGIN:VEVENT
SUMMARY:Republic Day HOliday
DTSTART;VALUE=DATE:20250127
DTEND;VALUE=DATE:20250128
DESCRIPTION:Holiday-Republic Day 
END:VEVENT
BEGIN:VEVENT
SUMMARY:Ramzan Holiday
DTSTART;VALUE=DATE:20250331
DTEND;VALUE=DATE:20250401
DESCRIPTION:Holiday0Ramzan
END:VEVENT
BEGIN:VEVENT
SUMMARY:Bakrid Holiday
DTSTART;VALUE=DATE:20250609
DTEND;VALUE=DATE:20250610
DESCRIPTION:Bakrid holiday
END:VEVENT
BEGIN:VEVENT
SUMMARY:Holiday-Independence Day
DTSTART;VALUE=DATE:20250815
DTEND;VALUE=DATE:20250816
DESCRIPTION:Independence Day holiday
END:VEVENT
BEGIN:VEVENT
SUMMARY:Holiday-Ganesh Chathurthi
DTSTART;VALUE=DATE:20250827
DTEND;VALUE=DATE:20250828
DESCRIPTION:Ganesh Chathurthi holiday instead of Vijayadashami due to overlap with Gandhi Jayanti. This change is not applicable for the 2026 holiday calendar.
END:VEVENT
BEGIN:VEVENT
SUMMARY:Holiday-Gandhi Jayanthi
DTSTART;VALUE=DATE:20251002
DTEND;VALUE=DATE:20251003
DESCRIPTION:Gandhi Jayanthi holiday
END:VEVENT
BEGIN:VEVENT
SUMMARY:Holiday-Diwali
DTSTART;VALUE=DATE:20251020
DTEND;VALUE=DATE:20251021
DESCRIPTION:Diwali holiday
END:VEVENT
BEGIN:VEVENT
SUMMARY:Holiday-Christmas
DTSTART;VALUE=DATE:20251225
DTEND;VALUE=DATE:20251226
DESCRIPTION:Christmas holiday
END:VEVENT
END:VCALENDAR`;

      // Parse the ICS file using ical.js
      const jcalData = ICAL.parse(icsText);
      const comp = new ICAL.Component(jcalData);

      // Extract events
      const events = comp.getAllSubcomponents("vevent");
      const parsedEvents = events
        .map((vevent) => {
          const summary = vevent.getFirstPropertyValue("summary");
          const dtstart = vevent.getFirstPropertyValue("dtstart");
          const dtend = vevent.getFirstPropertyValue("dtend");
          const description = vevent.getFirstPropertyValue("description");

          // Null check: Ensure that the properties are not null before accessing them
          if (!summary || !dtstart || !dtend) {
            console.error("Event data missing critical information.");
            return null; // Skip this event if required data is missing
          }

          // Get the local time zone
          const localTimeZone = getLocalTimeZone();

          // Use full date-time for events
          const startDate = dtstart.toString(); // Full date-time format
          const endDate = dtend.toString(); // Full date-time format

          return {
            subject: summary.toString(),
            start: {
              dateTime: startDate,
              timeZone: localTimeZone,
            },
            end: {
              dateTime: endDate,
              timeZone: localTimeZone,
            },
            isAllDay: true,
            body: {
              content: description ? description.toString() : "No description provided",
              contentType: "text",
            },
          };
        })
        .filter((event) => event !== null); // Filter out null events

      // Step 2: User Login to get Access Token
      const loginResponse = await msalInstance.loginPopup(loginRequest);
      const accessToken = loginResponse.accessToken;
      console.log("Parsed Events:", parsedEvents);
      if (!parsedEvents || parsedEvents.length === 0) {
        alert("No event details found from the ICS file.");
        return;
      }

      // Step 3: Call Microsoft Graph API to create the events
      for (const event of parsedEvents) {
        if (event && event.subject) {
          const response = await fetch("https://graph.microsoft.com/v1.0/me/events", {
            method: "POST",
            headers: {
              Authorization: `Bearer ${accessToken}`, // Corrected Bearer token formatting
              "Content-Type": "application/json",
            },
            body: JSON.stringify(event),
          });

          if (response.ok) {
            console.log(`${event.subject} added to Outlook Calendar successfully!`);
          } else {
            console.error("Failed to add event:", await response.json());
          }
        } else {
          console.error("Event subject is missing.");
        }
      }

      alert("All events added to Outlook Calendar!");
    } catch (error) {
      console.error("Error handling events:", error);
    }
  };

  return (
    <div>
      <h2>Add Holiday Events to Outlook Calendar from ICS File</h2>
      <button onClick={handleAddEvents} disabled={false}>
        Add All Events to Outlook Calendar
      </button>
    </div>
  );
};

export default OutlookEvent;
